<template>
	<div class="justify-between flex flex-grow" style="flex-direction: column" v-if="isUserAllowedInLevel5or6">
		<BaseHeader :title="$t('new_sign_request')" />
		<div class="m-8 h-full" v-if="hasCustomerMain">
			<div class="flex flex-grow space-x-12">
				<BaseSelect
					class="w-1/5 mb-6"
					ref="BaseSelect"
					:required="false"
					:label="$t('pick_file')"
					:options="documents"
					v-model="selectedDocument"
					@input="pickDocument"
					:allow-empty="false"
				/>
				<DocumentFileUploader
					v-if="!isAppContainer"
					ref="DocumentFileUploader"
					:instant-upload="false"
					class="cursor-pointer mb-6"
					style="display: none"
					@input="uploadDocument"
					accept="application/pdf"
				/>
				<div v-if="image != null">
					<BaseButton @click="pushSignLocation" class="mt-8 mb-6">
						<font-awesome-icon :icon="['fas', 'expand']" class="text-gray-700 mr-2" />
						{{ $t('add_sign') }}
					</BaseButton>
				</div>
			</div>
			<VuePdfEmbed ref="myPdfComponent" :source="image" style="display: none" v-if="image != null" />
			<div style="align-items: start;" class="flex flex-wrap gap-8" v-if="pdfPageCount > 0">
				<div v-for="page in pdfPageCount" :key="page" style="width: 709px; height: 1000px;">
					<div
						class="shadow-md mr-6 border"
						style="align-items: start; position: absolute; display: flex; border-width: 3px"
						:style="selectedPage === page ? 'border-color: #0068B4' : 'border-color: #e2e8f0'"
						@click="selectedPage = page"
					>
						<VuePdfEmbed class="z-0" :page="page" height="1000" width="709" :source="image"></VuePdfEmbed>
						<div class="z-10" style="position: absolute; height: 1000px; width: 709px" @click="selectedSign = null">
							<vue-draggable-resizable
								v-for="signLocation in getSignsForPage(page)"
								v-model="dragElement"
								:handles="['tl', 'tr', 'bl', 'br']"
								:w="252"
								:h="63"
								:min-width="252"
								:min-height="63"
								:parent="true"
								:max-width="500"
								:max-height="100"
								:key="signLocation.id"
								class="cursor-pointer"
								@start="drag = true"
								@resizing="(x, y, eWidth, eHeight) => onResize(x, y, eWidth, eHeight, signLocation.id)"
								@dragstop="(x, y) => onDragStop(x, y, signLocation.id)"
							>
								<div
									class="border-4 justify-center italic"
									@click.stop="selectedSign = signLocation.id"
									:style="`width: ${signLocation.width}px; height: ${signLocation.height}px`"
									style="border-color: red; min-height: 25px; min-width: 170px; text-align: center; max-height: 100px; max-width: 500px;"
								>
									{{ $t('sign') }}
								</div>
							</vue-draggable-resizable>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="m-8 flex flex-row justify-start h-full" v-else>
			<div class="text-black font-bold">
				{{ $t('customer_app_not_installed') }}
			</div>
		</div>
		<Footer class="mx-8 flex flex-shrink" :is-skippable="false" @backButtonClick="$router.go(-1)"
			><template #continueButton>
				<SaveButton @click="save" class="w-16 btn-blue" :disabled="signLocations.length <= 0">
					{{ $t('general.save') }}
				</SaveButton>
			</template></Footer
		>
		<router-view />
	</div>
	<Forbidden v-else />
</template>

<script>
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden';
import BaseButton from '@/components/forms/buttons/BaseButton';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader.vue';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Footer from '@/components/damagecases/Footer.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import { CustomersApiFactory, PdfApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';

export default {
	components: { BaseHeader, BaseSelect, SaveButton, DocumentFileUploader, BaseButton, Forbidden, Footer, VuePdfEmbed },
	name: 'ProcessDocumentSign',
	mixins: [Authentication, LoadingSpinner, AppContainerMixin],
	data() {
		return {
			image: null,
			isDocument: false,
			drag: false,
			dragElement: null,
			width: 252,
			height: 63,
			signLocations: [],
			selectedSign: null,
			pdfPageCount: 0,
			selectedPage: 1,
			documents: [],
			selectedDocument: null,
			hasCustomerMain: true,
			pdfSize: {
				width: 0,
				height: 0,
			},
			isSending: false,
			pauseEventListener: false,
		};
	},
	created() {
		if (this.isUserAllowedInLevel5or6) {
			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customersIdGet(this.$route.params.customerId)
				.then(response => {
					this.hasCustomerMain = response.data.customer_main !== null;
				});
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			document.addEventListener('keyup', e => {
				if (e.key === 'Backspace' || e.key === 'Delete') {
					if (!this.pauseEventListener) {
						this.removeSignLocation();
					}
				}
			});
			this.$store.dispatch('updateDocuments', { id: this.$route.params.processId });

			setTimeout(() => {
				const tempDocs = this.$store.getters.getDocumentsRepairShop.filter(doc => doc.uuid != null);
				this.documents = tempDocs.map(element => {
					return {
						display: element.label,
						value: {
							uuid: element.uuid,
							type: element.type,
						},
					};
				});
				this.documents.push({ display: 'Neues Dokument hochladen', value: { uuid: 'new', type: 'misc' } });
				this.documents.reverse();
			}, 500);
		}
	},
	methods: {
		getSignsForPage(page) {
			return this.signLocations.filter(element => element.page === page);
		},
		getPdfSize() {
			this.$nextTick(() => {
				const pdfElement = this.$refs.myPdfComponent.$el.querySelector('canvas');
				if (pdfElement) {
					this.pdfSize.width = pdfElement.width;
					this.pdfSize.height = pdfElement.height;
				} else {
					console.error('Unable to find PDF element');
				}
			});
		},
		save() {
			if (this.selectedDocument.value.type === 'misc') {
				this.pauseEventListener = true;
				this.$swal({
					title: this.$t('file_name').toString(),
					text: this.$t('enter_file_name').toString(),
					icon: 'question',
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('confirm').toString(),
					input: 'text',
					inputValidator: value => {
						if (!value) {
							return this.$t('enter_name');
						}
					},
				}).then(result => {
					if (result.isDismissed) {
						this.pauseEventListener = false;
					}
					if (result.value) {
						this.makeApiCall(result.value);
					}
				});
			} else {
				this.makeApiCall(this.selectedDocument.display);
			}
		},
		makeApiCall(miscName) {
			this.$swal({
				title: this.$t('sure').toString(),
				text: this.$t('sign_request_sure').toString(),
				icon: 'warning',
				cancelButtonText: this.$t('general.cancel').toString(),
				showCancelButton: true,
				confirmButtonText: this.$t('forward').toString(),
			}).then(result => {
				if (result.value) {
					this.isSending = true;
					// constants for the displayed size of the PDF in the Vue.js app
					const displayedPdfWidth = 709; // width of the PDF as it is displayed in the Vue.js app
					const displayedPdfHeight = 1000; // height of the PDF as it is displayed in the Vue.js app

					// scaling factors based on the actual size of the PDF and the displayed size
					const scaleX = this.pdfSize.width / displayedPdfWidth;
					const scaleY = this.pdfSize.height / displayedPdfHeight;

					let signatureFieldWidth = 252; // actual width of signature field in pixels
					let signatureFieldHeight = 63; // actual height of signature field in pixels

					// calculate the aspect ratio of the signature field

					const parsedSigns = [];
					this.signLocations.forEach(element => {
						signatureFieldHeight = element.height;
						signatureFieldWidth = element.width;

						const aspectRatio = signatureFieldWidth / signatureFieldHeight;

						// scale the positions and dimensions to match the actual PDF size
						const scaledX = element.x * scaleX;
						const scaledY = element.y * scaleY;
						const scaledWidth = signatureFieldWidth * scaleX; // use the actual width scaled
						const scaledHeight = scaledWidth / aspectRatio; // calculate height based on the scaled width to maintain aspect ratio

						// convert the scaled coordinates to percentages of the PDF dimensions
						const percentX = (scaledX / this.pdfSize.width) * 100;
						const percentY = (scaledY / this.pdfSize.height) * 100;
						const percentWidth = (scaledWidth / this.pdfSize.width) * 100;
						const percentHeight = (scaledHeight / this.pdfSize.height) * 100;

						// calculate the coordinates of the signature field corners
						element.coordinates.a = { x: percentX, y: percentY };
						element.coordinates.b = { x: percentX + percentWidth, y: percentY };
						element.coordinates.c = { x: percentX, y: percentY + percentHeight };
						element.coordinates.d = { x: percentX + percentWidth, y: percentY + percentHeight };

						// add the signature field data to the parsedSigns array
						parsedSigns.push({ coordinates: element.coordinates, page: element.page });
					});

					const request = {
						base64content: this.image,
						document_typ: this.selectedDocument.value.type,
						signs: parsedSigns,
					};
					if (miscName != null) {
						request['misc_name'] = miscName;
					} else {
						request['document_uuid'] = this.selectedDocument.value.uuid;
					}
					PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.pdfDamagecasesIdSignableDocumentPost(this.$route.params.processId, request)
						.then(response => {
							this.$swal(this.$t('general.success').toString(), this.$t('request_sent_to_customer').toString(), 'success').then(() => {
								this.isSending = false;
								this.$store.commit('setConfirmLeaving', false);
								this.$router.go(-1);
							});
						})
						.catch(e => {
							this.isSending = false;
							this.$swal(this.$t('general.error').toString(), this.$t('request_not_saved').toString(), 'error');
						});
				} else {
					this.pauseEventListener = false;
				}
			});
		},
		pickDocument() {
			if (this.selectedDocument.value.uuid === 'new') {
				this.$refs.DocumentFileUploader.openUpload();
			} else {
				let base64Data = '';
				new PDFDamagecasesRepository()
					.getSavedDocument(this.selectedDocument.value.uuid)
					.then(response => {
						const blob = new Blob([response.data], { type: 'application/pdf' });
						const reader = new FileReader();
						reader.readAsDataURL(blob);
						reader.onloadend = function() {
							base64Data = reader.result;
						};
					})
					.finally(_ => {
						setTimeout(() => {
							this.uploadDocument(base64Data);
						}, 200);
					});
			}
		},
		pushSignLocation() {
			this.signLocations.push({
				id: this.signLocations.length,
				page: this.selectedPage,
				width: this.width,
				height: this.height,
				x: 0,
				y: 0,
				coordinates: {},
			});
		},
		removeSignLocation() {
			if (this.selectedSign != null && !this.isSending) {
				this.signLocations = this.signLocations.filter(element => element.id !== this.selectedSign);
			}
		},
		onDragStop(x, y, id) {
			const element = this.signLocations.find(element => element.id === id);
			if (element != null) {
				element.x = x;
				element.y = y;
			}
		},
		onResize(x, y, width, height, id) {
			const element = this.signLocations.find(element => element.id === id);
			if (element != null) {
				element.height = height;
				element.width = width;
				element.x = x;
				element.y = y;
			}
		},
		uploadDocument($event) {
			this.image = $event;
			setTimeout(() => {
				this.pdfPageCount = this.$refs.myPdfComponent.pageCount;
			}, 500);
		},
	},
	watch: {
		image(newValue, oldValue) {
			if (newValue !== null) {
				//TODO: more advanced approach? change with using mutation observer
				//delay needed to get pdf size (watch gets called before pdf is rendered)
				setTimeout(() => {
					this.getPdfSize();
				}, 1200); // Adjust the delay as needed
			}
		},
	},
};
</script>

<style scoped></style>
